import type { CSSProperties } from "react";
import { _ } from "i18n";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { isDiscoverGamesGridCellEmpty } from "../formatters";
import { DiscoverGameStatus, gameStatusGradeColor } from "../gameStatus";
import type { DiscoverGamesClubMetadataOption, GameRow } from "../types";
import { discoverGameRowId } from "../rowHelpers";

export function DiscoverGamesGameStatusDropdownCell({
	row,
	options,
	isUpdating = false,
	onChange,
	readOnlyTitle,
}: {
	row: GameRow;
	options: DiscoverGamesClubMetadataOption[];
	isUpdating?: boolean;
	onChange?: (gameId: number, gameStatus: string | null) => void;
	readOnlyTitle?: string;
}) {
	const gameId = discoverGameRowId(row);
	const raw = row.gameStatus;
	const selectedValue = isDiscoverGamesGridCellEmpty(raw) ? "" : String(raw);
	const allMappedOptions = options.map((item) => ({
		value: String(item.value),
		label: item.label,
	}));
	const transitionOptions = selectedValue === DiscoverGameStatus.DONE
		? allMappedOptions.filter((o) => o.value === DiscoverGameStatus.GAME_CREATED_WITH_ISSUES)
		: allMappedOptions;
	const selectedOption = selectedValue
		? allMappedOptions.find((item) => item.value === selectedValue) ?? null
		: null;
	// Keep the current value in the menu so the selected option is always part of `items`
	// (a DONE game only offers GAME_CREATED_WITH_ISSUES as a transition target).
	const mappedOptions = selectedOption && !transitionOptions.some((o) => o.value === selectedOption.value)
		? [selectedOption, ...transitionOptions]
		: transitionOptions;

	const gradeColor = gameStatusGradeColor(raw);

	if (!onChange || gameId === null || options.length === 0) {
		const displayText = selectedOption?.label ?? (!isDiscoverGamesGridCellEmpty(raw) ? String(raw) : "-");
		return (
			<div className="thinCell" title={readOnlyTitle}>
				{gradeColor ? (
					<span className="discoverGamesTableV3-gameStatusText" style={{ color: gradeColor }}>
						{displayText}
					</span>
				) : (
					displayText
				)}
			</div>
		);
	}

	return (
		<div className="thinCell">
			<div
				className={
					gradeColor
						? "discoverGamesTableV3-gameStatusGrade discoverGamesTableV3-gameStatusGrade--active"
						: "discoverGamesTableV3-gameStatusGrade"
				}
				style={
					gradeColor
						? ({ ["--dgs-game-status-color" as string]: gradeColor } as CSSProperties)
						: undefined
				}
			>
				<SelectComponent
					placeholder={_("discover.gamesTable.cellValue.none")}
					value={selectedOption}
					items={mappedOptions}
					isDisabled={isUpdating}
					isCleareable={false}
					menuPortalTarget
					handleChange={(option) => {
						if (option?.value === undefined || option.value === null || option.value === "") {
							onChange(gameId, null);
							return;
						}
						onChange(gameId, String(option.value));
					}}
				/>
			</div>
		</div>
	);
}
